import { API_URL } from '../config';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Search, Clock, User, Building2, CheckCircle, AlertCircle,
  Loader2, ChevronRight, History, MessageSquare
} from 'lucide-react';
import axios from 'axios';
import { useBranding } from '../context/BrandingContext';


const STATUS_COLORS = {
  'Em análise': { bg: '#e8f4fd', color: '#3498db', icon: Search },
  'Em andamento': { bg: '#fef9e7', color: '#f39c12', icon: Clock },
  'Aguardando informações': { bg: '#fdf2e9', color: '#e67e22', icon: MessageSquare },
  'Concluído': { bg: '#e8f8f0', color: '#27ae60', icon: CheckCircle },
  'Cancelado': { bg: '#fdeaea', color: '#e74c3c', icon: AlertCircle },
};

const AcompanharProtocolo = () => {
  // `protocolo` é a chave do link: o código aleatório enviado por e-mail (mostra
  // tudo) ou o número do protocolo (links antigos: só o status)
  const { protocolo } = useParams();
  const { branding } = useBranding();
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [data, setData] = useState(null);
  const [historico, setHistorico] = useState(null);
  const [showHistorico, setShowHistorico] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState('todos');

  useEffect(() => {
    carregarDados();
  }, [protocolo]);

  const carregarDados = async () => {
    setLoading(true);
    setErro(null);
    try {
      const response = await axios.get(`${API_URL}/acompanhar/${protocolo}`);
      // protocolo inexistente volta 200 com { erro } — antes a tela tentava
      // desenhar o card vazio e quebrava
      if (response.data?.erro || !response.data?.atendimento) {
        setErro(response.data?.erro || 'Protocolo não encontrado');
        return;
      }
      setData(response.data);
    } catch (error) {
      setErro(error.response?.data?.erro || 'Erro ao carregar dados do protocolo');
    } finally {
      setLoading(false);
    }
  };

  const carregarHistorico = async () => {
    if (historico) return; // Ja carregou
    try {
      const response = await axios.get(`${API_URL}/acompanhar/${protocolo}/historico`);
      setHistorico(response.data);
    } catch (error) {
      console.error('Erro ao carregar historico:', error);
    }
  };

  const formatarData = (dataIso) => {
    if (!dataIso) return '-';
    const data = new Date(dataIso);
    return data.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const StatusBadge = ({ status }) => {
    const config = STATUS_COLORS[status] || { bg: '#f5f5f5', color: '#666', icon: AlertCircle };
    const Icon = config.icon;
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        backgroundColor: config.bg,
        color: config.color,
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '14px',
        fontWeight: 500
      }}>
        <Icon size={16} />
        {status}
      </span>
    );
  };

  const AtendimentoCard = ({ atendimento, isMain = false }) => {
    const config = STATUS_COLORS[atendimento.status] || { bg: '#f5f5f5', color: '#666' };

    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: isMain ? '0 4px 20px rgba(0,0,0,0.1)' : '0 2px 8px rgba(0,0,0,0.05)',
        border: isMain ? `2px solid var(--primary)` : '1px solid #e5e7eb',
        marginBottom: '12px',
        transition: 'transform 0.2s, box-shadow 0.2s'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Protocolo</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>{atendimento.protocolo}</div>
          </div>
          <StatusBadge status={atendimento.status} />
        </div>

        {atendimento.assunto && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Assunto</div>
            <div style={{ color: '#333' }}>{atendimento.assunto}</div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
          {atendimento.setor !== undefined && (<>
          <div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              <Building2 size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
              Setor
            </div>
            <div style={{ color: '#333' }}>{atendimento.setor || '-'}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              <User size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
              Atendente
            </div>
            <div style={{ color: '#333' }}>{atendimento.atendente?.nome || 'A definir'}</div>
          </div>
          </>)}
          <div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              <Clock size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
              Criado em
            </div>
            <div style={{ color: '#333' }}>{formatarData(atendimento.criado_em)}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              <Clock size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
              Atualizado
            </div>
            <div style={{ color: '#333' }}>{formatarData(atendimento.atualizado_em)}</div>
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <Loader2 size={40} className="animate-spin" style={{ color: 'var(--primary)' }} />
          <p style={{ marginTop: '16px', color: '#666' }}>Carregando protocolo...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (erro) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <AlertCircle size={48} color="#e74c3c" />
          <h2 style={{ marginTop: '16px', color: '#e74c3c' }}>{erro}</h2>
          <p style={{ color: '#666', marginTop: '8px' }}>
            Verifique se o protocolo está correto ou entre em contato conosco.
          </p>
        </div>
      </div>
    );
  }

  // Filtrar atendimentos
  const atendimentosFiltrados = data?.outros_atendimentos?.filter(a => {
    if (filtroStatus === 'todos') return true;
    if (filtroStatus === 'abertos') return !['Concluído', 'Cancelado'].includes(a.status);
    if (filtroStatus === 'concluidos') return a.status === 'Concluído';
    return true;
  }) || [];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.logoContainer}>
          <div style={styles.logo}>{branding.nome_operadora || branding.nome}</div>
          <div style={styles.logoSubtitle}>Acompanhamento do seu atendimento</div>
        </div>
      </div>

      {/* Main Content */}
      <div style={styles.content}>
        {/* Saudacao */}
        {data?.beneficiario?.nome && (
          <div style={{ marginBottom: '20px', color: '#666' }}>
            Olá, <strong>{data.beneficiario.nome}</strong>!
          </div>
        )}

        {/* Atendimento Principal */}
        <h2 style={{ marginBottom: '16px', color: '#333', fontSize: '18px' }}>
          Seu Atendimento
        </h2>
        <AtendimentoCard atendimento={data.atendimento} isMain={true} />

        {data.limitado && (
          <div style={{ background: '#f5f7fa', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px 16px', color: '#555', fontSize: '14px', marginBottom: '24px' }}>
            Para ver o setor, quem está cuidando e o histórico, abra o link do e-mail
            mais recente que enviamos sobre este atendimento.
          </div>
        )}

        {/* Botao Historico (só pelo link do e-mail) */}
        {!data.limitado && (
        <button
          onClick={() => {
            setShowHistorico(!showHistorico);
            if (!showHistorico) carregarHistorico();
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: 'transparent',
            border: '1px solid #e5e7eb',
            padding: '10px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            color: '#666',
            fontSize: '14px',
            marginBottom: '24px',
            width: '100%',
            justifyContent: 'center'
          }}
        >
          <History size={16} />
          {showHistorico ? 'Ocultar histórico' : 'Ver histórico de movimentações'}
        </button>
        )}

        {/* Historico */}
        {showHistorico && historico && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ marginBottom: '12px', fontSize: '16px', color: '#333' }}>
              Histórico
            </h3>
            <div style={{ borderLeft: '2px solid #e5e7eb', paddingLeft: '16px' }}>
              {historico.historico?.map((item, index) => (
                <div key={item.id} style={{ marginBottom: '16px', position: 'relative' }}>
                  <div style={{
                    position: 'absolute',
                    left: '-21px',
                    top: '4px',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--primary)'
                  }} />
                  <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>
                    {formatarData(item.criado_em)}
                  </div>
                  <div style={{ color: '#333' }}>
                    {item.descricao}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    por {item.usuario}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Outros Atendimentos */}
        {data?.mostrar_todos && data?.outros_atendimentos?.length > 0 && (
          <div>
            <h2 style={{ marginBottom: '16px', color: '#333', fontSize: '18px' }}>
              Seus Outros Atendimentos
            </h2>

            {/* Filtros */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              {[
                { value: 'todos', label: 'Todos' },
                { value: 'abertos', label: 'Em andamento' },
                { value: 'concluidos', label: 'Concluídos' },
              ].map(filtro => (
                <button
                  key={filtro.value}
                  onClick={() => setFiltroStatus(filtro.value)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    border: 'none',
                    backgroundColor: filtroStatus === filtro.value ? 'var(--primary)' : '#f5f5f5',
                    color: filtroStatus === filtro.value ? 'white' : '#666',
                    cursor: 'pointer',
                    fontSize: '13px',
                    transition: 'all 0.2s'
                  }}
                >
                  {filtro.label}
                </button>
              ))}
            </div>

            {/* Lista */}
            {atendimentosFiltrados.map(atendimento => (
              <AtendimentoCard key={atendimento.id} atendimento={atendimento} />
            ))}

            {atendimentosFiltrados.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                Nenhum atendimento encontrado com este filtro.
              </div>
            )}
          </div>
        )}

        {/* Footer info */}
        <div style={{
          marginTop: '32px',
          padding: '16px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          textAlign: 'center',
          fontSize: '13px',
          color: '#666'
        }}>
          <p>Este acompanhamento é atualizado em tempo real.</p>
          <p>Para mais informações, entre em contato conosco informando seu protocolo.</p>
        </div>
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <p>&copy; {new Date().getFullYear()} {branding.nome_operadora || branding.nome} - Todos os direitos reservados</p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    backgroundColor: 'var(--primary)',
    padding: '20px 24px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  },
  logoContainer: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  logo: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: 'white',
  },
  logoSubtitle: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.8)',
  },
  content: {
    flex: 1,
    maxWidth: '800px',
    margin: '0 auto',
    padding: '24px',
    width: '100%',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50vh',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50vh',
    padding: '24px',
    textAlign: 'center',
  },
  footer: {
    backgroundColor: '#333',
    color: '#999',
    padding: '16px',
    textAlign: 'center',
    fontSize: '12px',
  },
};

export default AcompanharProtocolo;
